import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { DaylightCardLink } from "../../../../modules/content/components/DaylightCardLink/DaylightCardLink";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { IIconContentGridBlockProps } from "./IconContentGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;
const iconWashes = ["bg-sky", "bg-lilac", "bg-melon", "bg-mint"] as const;
export const IconContentGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: IIconContentGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-8 md:gap-10">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div
          className={cn(
            "grid grid-cols-1 overflow-hidden border-y border-cobalt/20 sm:grid-cols-2",
            columns[columnCount || 3],
          )}
        >
          {items.map((item, index) => (
            <DaylightCardLink
              className={cn(
                "min-h-64 border-b border-cobalt/20 bg-daylight p-7 transition-colors hover:bg-sky/35 md:p-8",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <span
                className={cn(
                  "inline-flex size-14 items-center justify-center rounded-full text-cobalt",
                  iconWashes[index % iconWashes.length],
                )}
              >
                <Icon className="size-6" name={item.icon} />
              </span>
              <h3 className="mt-16 max-w-[16ch] font-daylight-display text-2xl font-semibold leading-tight text-foreground">
                {item.heading}
              </h3>
              {item.description ? (
                <DaylightRichText
                  className="mt-4 text-base text-basalt md:text-lg"
                  content={item.description}
                />
              ) : null}
            </DaylightCardLink>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

IconContentGridBlock.displayName = "Block::IconContentGridBlock";
